// Поездки (Trip Planner, CTHS-147/189/190/191/193): тонкий слой над /api/trips.
// Логики здесь нет — только запросы и типы, зеркалящие DTO backend (зеркало DeclutterApi.ts).
import createAxiosInstance from '../support/AxiosSupport';

export type TripStatus = 'PLANNING' | 'PACKING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type TripItemStatus = 'TO_PACK' | 'PACKED' | 'SKIPPED';
export type TripChecklistCategory = 'DOCUMENTS' | 'MONEY' | 'TECH' | 'HYGIENE' | 'CLOTHING' | 'OTHER';

export interface TripEvent {
    eventDate: string;
    title: string;
    occasionCode?: string | null;
}

export interface TripListItem {
    id: string;
    name: string;
    city: string | null;
    country: string | null;
    startDate: string;
    endDate: string;
    status: TripStatus;
    dayCount: number;
    itemsTotal: number;
    itemsPacked: number;
    checklistTotal: number;
    checklistDone: number;
}

export interface TripDetail {
    id: string;
    name: string;
    city: string | null;
    country: string | null;
    locationCode: string | null;
    startDate: string;
    endDate: string;
    status: TripStatus;
    notes: string | null;
    events: TripEvent[];
    dayCount: number;
    allowedTransitions: TripStatus[];
}

export interface TripRequest {
    name: string;
    city?: string | null;
    country?: string | null;
    locationCode?: string | null;
    startDate: string;
    endDate: string;
    notes?: string | null;
    events?: TripEvent[];
}

/** Зеркало WeatherForecastDto: `estimated` — климатическая оценка, а не прогноз. */
export interface TripDayWeather {
    available: boolean;
    estimated?: boolean;
    reason?: string | null;
    tempMinC?: number | null;
    tempMaxC?: number | null;
    warmthCode?: string | null;
    seasonCode?: string | null;
    weatherSuitabilityCodes?: string[] | null;
    locationLabel?: string | null;
}

export interface TripDayItem {
    id: string;
    name: string | null;
    primaryPhotoUrl: string | null;
}

export interface TripDay {
    id: string;
    date: string;
    eventLabel: string | null;
    occasionCode: string | null;
    locked: boolean;
    weather: TripDayWeather | null;
    items: TripDayItem[];
}

export interface TripOutfitPlan {
    tripId: string;
    days: TripDay[];
    lockedDays: number;
}

export interface TripItem {
    id: string;
    clothingItemId: string;
    name: string | null;
    primaryPhotoUrl: string | null;
    status: TripItemStatus;
    daysUsed: number;
}

export interface TripItemList {
    tripId: string;
    items: TripItem[];
    totalItems: number;
    packedCount: number;
    skippedCount: number;
    packedPercent: number;
    totalDays: number;
}

export interface TripChecklistItem {
    id: string;
    category: TripChecklistCategory;
    title: string;
    done: boolean;
    sortOrder: number;
}

export interface TripChecklistGroup {
    category: TripChecklistCategory;
    items: TripChecklistItem[];
    doneCount: number;
    totalItems: number;
}

export interface TripChecklist {
    tripId: string;
    groups: TripChecklistGroup[];
    totalItems: number;
    doneCount: number;
    donePercent: number;
    clothingTotal: number;
    clothingPacked: number;
}

export const TRIP_STATUS_LABELS: Record<TripStatus, string> = {
    PLANNING: 'Планируется',
    PACKING: 'Сборы',
    IN_PROGRESS: 'В пути',
    COMPLETED: 'Завершена',
    CANCELLED: 'Отменена',
};

export const TRIP_ITEM_STATUS_LABELS: Record<TripItemStatus, string> = {
    TO_PACK: 'Собрать',
    PACKED: 'Упаковано',
    SKIPPED: 'Не беру',
};

export const TRIP_CHECKLIST_CATEGORY_LABELS: Record<TripChecklistCategory, string> = {
    DOCUMENTS: 'Документы',
    MONEY: 'Деньги',
    TECH: 'Техника',
    HYGIENE: 'Гигиена',
    CLOTHING: 'Одежда',
    OTHER: 'Прочее',
};

export async function fetchTrips(status?: TripStatus | null): Promise<TripListItem[]> {
    const api = await createAxiosInstance();
    return (await api.get<TripListItem[]>('/trips', {params: status ? {status} : {}})).data;
}

export async function fetchTrip(tripId: string): Promise<TripDetail> {
    const api = await createAxiosInstance();
    return (await api.get<TripDetail>(`/trips/${tripId}`)).data;
}

export async function createTrip(request: TripRequest): Promise<TripDetail> {
    const api = await createAxiosInstance();
    return (await api.post<TripDetail>('/trips', request)).data;
}

export async function updateTrip(tripId: string, request: TripRequest): Promise<TripDetail> {
    const api = await createAxiosInstance();
    return (await api.put<TripDetail>(`/trips/${tripId}`, request)).data;
}

export async function deleteTrip(tripId: string): Promise<void> {
    const api = await createAxiosInstance();
    await api.delete(`/trips/${tripId}`);
}

export async function changeTripStatus(tripId: string, status: TripStatus): Promise<TripDetail> {
    const api = await createAxiosInstance();
    return (await api.patch<TripDetail>(`/trips/${tripId}/status`, {status})).data;
}

export async function fetchOutfitPlan(tripId: string): Promise<TripOutfitPlan> {
    const api = await createAxiosInstance();
    return (await api.get<TripOutfitPlan>(`/trips/${tripId}/outfit-plan`)).data;
}

export async function generateOutfitPlan(tripId: string): Promise<TripOutfitPlan> {
    const api = await createAxiosInstance();
    return (await api.post<TripOutfitPlan>(`/trips/${tripId}/outfit-plan`, {})).data;
}

export async function overrideTripDay(tripId: string, dayId: string, itemIds: string[]): Promise<TripOutfitPlan> {
    const api = await createAxiosInstance();
    return (await api.patch<TripOutfitPlan>(`/trips/${tripId}/outfit-plan/${dayId}`, {itemIds})).data;
}

export async function unlockTripDay(tripId: string, dayId: string): Promise<TripOutfitPlan> {
    const api = await createAxiosInstance();
    return (await api.delete<TripOutfitPlan>(`/trips/${tripId}/outfit-plan/${dayId}/lock`)).data;
}

export async function fetchTripItemList(tripId: string): Promise<TripItemList> {
    const api = await createAxiosInstance();
    return (await api.get<TripItemList>(`/trips/${tripId}/item-list`)).data;
}

export async function updateTripItemStatus(tripId: string, itemId: string,
                                           status: TripItemStatus): Promise<TripItem> {
    const api = await createAxiosInstance();
    return (await api.patch<TripItem>(`/trips/${tripId}/item-list/${itemId}/status`, {status})).data;
}

export async function fetchTripChecklist(tripId: string): Promise<TripChecklist> {
    const api = await createAxiosInstance();
    return (await api.get<TripChecklist>(`/trips/${tripId}/checklist`)).data;
}

export async function addChecklistItem(tripId: string, category: TripChecklistCategory,
                                       title: string): Promise<TripChecklistItem> {
    const api = await createAxiosInstance();
    return (await api.post<TripChecklistItem>(`/trips/${tripId}/checklist`, {category, title})).data;
}

export async function updateChecklistItem(tripId: string, itemId: string,
                                          patch: {title?: string; done?: boolean; category?: TripChecklistCategory}
): Promise<TripChecklistItem> {
    const api = await createAxiosInstance();
    return (await api.patch<TripChecklistItem>(`/trips/${tripId}/checklist/${itemId}`, patch)).data;
}

export async function deleteChecklistItem(tripId: string, itemId: string): Promise<void> {
    const api = await createAxiosInstance();
    await api.delete(`/trips/${tripId}/checklist/${itemId}`);
}

/** Человеческий диапазон дат поездки: «12 – 19 октября 2026». */
export function formatTripDates(startDate: string, endDate: string): string {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return `${startDate} — ${endDate}`;
    }
    const sameYear = start.getFullYear() === end.getFullYear();
    const startText = start.toLocaleDateString('ru-RU',
        sameYear ? {day: 'numeric', month: 'short'} : {day: 'numeric', month: 'short', year: 'numeric'});
    const endText = end.toLocaleDateString('ru-RU', {day: 'numeric', month: 'short', year: 'numeric'});
    return `${startText} — ${endText}`;
}

/** Короткая подпись погоды дня; null — погоды нет (план строился без неё). */
export function formatDayWeather(weather: TripDayWeather | null | undefined): string | null {
    if (!weather || !weather.available) {
        return null;
    }
    const min = weather.tempMinC;
    const max = weather.tempMaxC;
    if (min === null || min === undefined || max === null || max === undefined) {
        return weather.estimated ? 'оценка по сезону' : null;
    }
    const range = `${Math.round(min)}…${Math.round(max)} °C`;
    return weather.estimated ? `${range} (оценка по сезону)` : range;
}

// --- Проверка сборов по фото (CTHS-211) -------------------------------------------------

export type TripPackingCheckStatus = 'PENDING' | 'PROCESSING' | 'READY' | 'ERROR';

export interface TripPackingCheckMatch {
    tripItemId: string;
    clothingItemId: string;
    name: string | null;
    primaryPhotoUrl: string | null;
    /** Уверенность модели 0..1; null у группы «не хватает» — о ней модель не говорила. */
    confidence: number | null;
    note: string | null;
}

export interface TripPackingCheckExtra {
    label: string;
    note: string | null;
}

export interface TripPackingCheck {
    id: string;
    tripId: string;
    status: TripPackingCheckStatus;
    found: TripPackingCheckMatch[];
    uncertain: TripPackingCheckMatch[];
    missing: TripPackingCheckMatch[];
    extra: TripPackingCheckExtra[];
    errorMessage: string | null;
    createdAt: string | null;
    appliedAt: string | null;
}

/** Проверка ещё в работе — результат появится позже. */
export const isPackingCheckInProgress = (status?: TripPackingCheckStatus | null): boolean =>
    status === 'PENDING' || status === 'PROCESSING';

/** Подпись уверенности: «похоже на …» честнее процента, который люди читают как факт. */
export function formatConfidence(confidence: number | null | undefined): string | null {
    if (confidence === null || confidence === undefined) {
        return null;
    }
    return `уверенность ${Math.round(confidence * 100)}%`;
}

export async function startPackingCheck(tripId: string, file: File): Promise<TripPackingCheck> {
    const api = await createAxiosInstance();
    const form = new FormData();
    form.append('file', file);
    return (await api.post<TripPackingCheck>(`/trips/${tripId}/packing-check`, form,
        {headers: {'Content-Type': 'multipart/form-data'}})).data;
}

export async function fetchPackingCheck(tripId: string, checkId: string): Promise<TripPackingCheck> {
    const api = await createAxiosInstance();
    return (await api.get<TripPackingCheck>(`/trips/${tripId}/packing-check/${checkId}`)).data;
}

/** Последняя проверка поездки; null — проверок ещё не было (backend отвечает 204). */
export async function fetchLatestPackingCheck(tripId: string): Promise<TripPackingCheck | null> {
    const api = await createAxiosInstance();
    const response = await api.get<TripPackingCheck | ''>(`/trips/${tripId}/packing-check/latest`);
    return response.status === 204 || !response.data ? null : (response.data as TripPackingCheck);
}

export async function applyPackingCheck(tripId: string, checkId: string,
                                        tripItemIds: string[]): Promise<TripItemList> {
    const api = await createAxiosInstance();
    return (await api.post<TripItemList>(`/trips/${tripId}/packing-check/${checkId}/apply`,
        {tripItemIds})).data;
}

// --- Итоги поездки (CTHS-213) -----------------------------------------------------------

export interface TripAnalyticsItem {
    tripItemId: string;
    clothingItemId: string;
    name: string | null;
    primaryPhotoUrl: string | null;
    status: TripItemStatus;
    daysUsed: number;
    wasUsed: boolean | null;
    unusedTripCount: number;
    strongHint: boolean;
}

export interface TripAnalyticsDay {
    dayId: string;
    date: string;
    eventLabel: string | null;
    plannedItems: TripAnalyticsItem[];
    /** Идентификаторы вещей гардероба, отмеченных надетыми в этот день. */
    wornItemIds: string[];
}

export interface TripAnalytics {
    tripId: string;
    reportedAt: string | null;
    days: TripAnalyticsDay[];
    taken: TripAnalyticsItem[];
    used: TripAnalyticsItem[];
    unnecessary: TripAnalyticsItem[];
    skipped: TripAnalyticsItem[];
    recommendations: TripAnalyticsItem[];
    daysCount: number;
    takenCount: number;
    usedCount: number;
    unnecessaryCount: number;
}

export interface TripWearReportDay {
    date: string;
    itemIds: string[];
}

export async function fetchTripAnalytics(tripId: string): Promise<TripAnalytics> {
    const api = await createAxiosInstance();
    return (await api.get<TripAnalytics>(`/trips/${tripId}/analytics`)).data;
}

export async function submitTripWearReport(tripId: string,
                                           days: TripWearReportDay[]): Promise<TripAnalytics> {
    const api = await createAxiosInstance();
    return (await api.post<TripAnalytics>(`/trips/${tripId}/wear-report`, {days})).data;
}
